import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import classnames from "classnames";
import { toggledFeatureProductEntitlements } from "utils/feature";
import HoverIconButton from "components/HoverIconButton";
import Modal from "components/Modal";
import { TOOLTIP_POSITION } from "utils/constants/ui";
import cloneDeep from "lodash/cloneDeep";
import {
  removeFeatureFromFeatureGroup,
  canTurnAllFeatureProductsOffRow
} from "utils/feature";
import FeatureForm from "../FeatureForm";
import Tooltip from "components/Tooltip";
import Analytics, {
  TOGGLE_ALL_PRODUCTS_FOR_FEATURE,
  REMOVE_FEATURE,
  SHOW_FEATURE_EDIT
} from "utils/analytics";
import { getItemDraggingProps } from "utils/drag";

const ENTITLEMENTS = `entitlements`;

const FeatureTitleCell = ({
  tracking,
  feature,
  featureGroup,
  products,
  thWidth,
  snapshot,
  provided
}) => {
  const [showModal, setShowModal] = useState(false);
  const [hoverCell, setHoverCell] = useState(false);
  const [hoverValue, setHoverValue] = useState(false);

  const {
    values,
    touched,
    setFieldValue,
    setValues,
    setTouched,
    setFieldTouched
  } = useFormikContext();
  const isTouched = Boolean(touched[ENTITLEMENTS]);
  const entitlements = values.entitlements;
  const canTurnAllOff = canTurnAllFeatureProductsOffRow({
    feature,
    products,
    entitlements
  });
  const featureIx = values.features.findIndex(
    ({ uuid }) => uuid === feature.uuid
  );

  const containerProps = getItemDraggingProps(
    {
      className: classnames("tl fw4 bb bl br hairline-1 relative", {
        "bg-white": !hoverCell && !snapshot.isDragging,
        "bg-content--secondary": hoverCell && !snapshot.isDragging,
        "dib ba fl": snapshot.isDragging
      }),
      onMouseEnter: () => !hoverCell && setHoverCell(true),
      onMouseLeave: () => hoverCell && setHoverCell(false),
      style: {
        width: `${thWidth}%`
      }
    },
    provided,
    snapshot
  );

  /**
   * Toggle all features entitlements for products
   */
  const toggleAllFeatureProducts = () => {
    Analytics.track(TOGGLE_ALL_PRODUCTS_FOR_FEATURE, tracking);

    const updatedEntitlements = toggledFeatureProductEntitlements({
      feature,
      products,
      entitlements,
      enabled: !canTurnAllOff
    });

    setFieldValue(ENTITLEMENTS, updatedEntitlements);
    if (!isTouched) {
      setFieldTouched(ENTITLEMENTS, true);
    }
  };

  const valueProps = {
    className: classnames(
      "f6 f5-ns pointer dib pv3 user-select-n link__underline pr2",
      {
        "pl3-ns": !hoverCell
      }
    ),
    onClick: () => {
      setShowModal(true);
      setHoverCell(false);
    },
    onMouseEnter: () => !hoverValue && setHoverValue(true),
    onMouseLeave: () => hoverValue && setHoverValue(false)
  };

  const updateFeature = (featureValues) => {
    Analytics.track(SHOW_FEATURE_EDIT, tracking);
    const updatedFeature = { ...feature, ...featureValues };
    const updatePath = `features[${featureIx}]`;
    setFieldValue(updatePath, updatedFeature);
    setFieldTouched(updatePath, true);
    setShowModal(false);
  };
  const title = <div {...valueProps}>{feature.title}</div>;
  const titleContent = feature.tooltip ? (
    <Tooltip
      id={`${featureGroup.uuid}.${feature.uuid}`}
      tooltip={feature.tooltip}
    >
      {title}
    </Tooltip>
  ) : (
    title
  );

  const removeButton = (
    <HoverIconButton
      onClick={() => {
        Analytics.track(REMOVE_FEATURE, tracking);
        removeFeatureFromFeatureGroup({
          values: cloneDeep(values),
          setValues,
          setTouched,
          draggableId: feature.uuid,
          sourceDropId: featureGroup.uuid
        });
      }}
      type="close"
      size={20}
    />
  );

  return (
    <th {...containerProps}>
      <div className="dtc flex flex-row items-center w-100 justify-between">
        <div className="flex items-center">
          {hoverCell && (
            <div className="dn flex-ns h-100 items-center justify-center">
              {removeButton}
            </div>
          )}
          <div className="flex dn-ns h-100 items-center justify-center">
            {removeButton}
          </div>
          {titleContent}
        </div>
        {hoverCell && (
          <div className="dn flex-ns h-100 items-center justify-center">
            <HoverIconButton
              onClick={toggleAllFeatureProducts}
              type="right"
              tooltipPosition={TOOLTIP_POSITION.LEFT}
              tooltip={`Toggle all ${canTurnAllOff ? "off" : "on"}`}
              size={20}
            />
          </div>
        )}
      </div>
      {showModal && (
        <Modal showModal={showModal} onClose={() => setShowModal(false)}>
          <FeatureForm
            feature={feature}
            setShowModal={setShowModal}
            updateFeature={updateFeature}
            featureGroup={featureGroup}
          />
        </Modal>
      )}
    </th>
  );
};

FeatureTitleCell.propTypes = {
  feature: PropTypes.object,
  featureGroup: PropTypes.object,
  products: PropTypes.array,
  thWidth: PropTypes.number,
  tdWidth: PropTypes.number
};

export default FeatureTitleCell;
